import type {
  RequestLatencyDistribution,
  WorkloadMetricsEntry,
  WorkloadMetricsInterval,
} from '@/lib/urm-api';
import { buildWindowAverageRequestTimes } from './logic';
import type { GroupingDimension, WorkloadRequestTimeSummary } from './types';

export type LatencyBucketKey = keyof RequestLatencyDistribution;

export const LATENCY_BUCKETS: ReadonlyArray<{
  key: LatencyBucketKey;
  label: string;
  lowerMs: number;
  upperMs: number | null;
}> = [
  { key: 'lt_25_ms', label: '<25 ms', lowerMs: 0, upperMs: 25 },
  { key: '25_to_50_ms', label: '25–50 ms', lowerMs: 25, upperMs: 50 },
  { key: '50_to_100_ms', label: '50–100 ms', lowerMs: 50, upperMs: 100 },
  { key: '100_to_250_ms', label: '100–250 ms', lowerMs: 100, upperMs: 250 },
  { key: '250_to_500_ms', label: '250–500 ms', lowerMs: 250, upperMs: 500 },
  { key: '500_to_1000_ms', label: '500 ms–1 s', lowerMs: 500, upperMs: 1_000 },
  { key: '1000_to_2500_ms', label: '1–2.5 s', lowerMs: 1_000, upperMs: 2_500 },
  { key: '2500_to_5000_ms', label: '2.5–5 s', lowerMs: 2_500, upperMs: 5_000 },
  { key: 'gte_5000_ms', label: '≥5 s', lowerMs: 5_000, upperMs: null },
];

export interface LatencySummary {
  counts: RequestLatencyDistribution;
  measuredRequests: number;
  p50Ms: number | null;
  p95Ms: number | null;
  p99Ms: number | null;
  slowerThan250Count: number;
  slowerThan1000Count: number;
  slowerThan250Percent: number;
  slowerThan1000Percent: number;
}

export interface LatencyTimelinePoint extends LatencySummary {
  timestamp: number;
  bucketPercentages: Record<LatencyBucketKey, number | null>;
}

export interface LatencyCoverage {
  entriesWithDistribution: number;
  totalEntries: number;
  requestCountWithDistribution: number;
  totalRequestCount: number;
  coveragePercent: number;
}

export interface LatencyGroupComparison extends LatencySummary {
  key: string;
  label: string;
  groupValues: Partial<Record<GroupingDimension, string>>;
  slowerThan250To1000Percent: number;
}

export interface WorkloadLatencySummary extends WorkloadRequestTimeSummary {
  measuredLatencyRequests: number;
  histogramP50Ms: number | null;
  histogramP95Ms: number | null;
  histogramP99Ms: number | null;
  slowerThan250Count: number;
  slowerThan1000Count: number;
  slowerThan250Percent: number | null;
  slowerThan1000Percent: number | null;
}

function emptyDistribution(): RequestLatencyDistribution {
  return {
    lt_25_ms: 0,
    '25_to_50_ms': 0,
    '50_to_100_ms': 0,
    '100_to_250_ms': 0,
    '250_to_500_ms': 0,
    '500_to_1000_ms': 0,
    '1000_to_2500_ms': 0,
    '2500_to_5000_ms': 0,
    gte_5000_ms: 0,
  };
}

function validCount(value: number): number {
  return Number.isFinite(value) ? Math.max(0, value) : 0;
}

function addDistribution(
  target: RequestLatencyDistribution,
  distribution: RequestLatencyDistribution,
): void {
  for (const bucket of LATENCY_BUCKETS) {
    target[bucket.key] += validCount(distribution[bucket.key]);
  }
}

function summarizeCounts(counts: RequestLatencyDistribution): LatencySummary {
  const measuredRequests = LATENCY_BUCKETS.reduce(
    (sum, bucket) => sum + counts[bucket.key],
    0,
  );
  const slowerThan250Count = LATENCY_BUCKETS
    .filter((bucket) => bucket.lowerMs >= 250)
    .reduce((sum, bucket) => sum + counts[bucket.key], 0);
  const slowerThan1000Count = LATENCY_BUCKETS
    .filter((bucket) => bucket.lowerMs >= 1_000)
    .reduce((sum, bucket) => sum + counts[bucket.key], 0);

  return {
    counts,
    measuredRequests,
    p50Ms: estimateLatencyPercentile(counts, 0.5),
    p95Ms: estimateLatencyPercentile(counts, 0.95),
    p99Ms: estimateLatencyPercentile(counts, 0.99),
    slowerThan250Count,
    slowerThan1000Count,
    slowerThan250Percent: measuredRequests === 0 ? 0 : slowerThan250Count / measuredRequests * 100,
    slowerThan1000Percent: measuredRequests === 0 ? 0 : slowerThan1000Count / measuredRequests * 100,
  };
}

/** Histogram quantile using linear interpolation inside bounded buckets. The
 * open-ended final bucket is represented by its 5-second lower bound. */
export function estimateLatencyPercentile(
  distribution: RequestLatencyDistribution,
  percentile: number,
): number | null {
  const total = LATENCY_BUCKETS.reduce(
    (sum, bucket) => sum + validCount(distribution[bucket.key]),
    0,
  );
  if (total === 0) return null;

  const rank = Math.min(1, Math.max(0, percentile)) * total;
  let cumulative = 0;
  for (const bucket of LATENCY_BUCKETS) {
    const count = validCount(distribution[bucket.key]);
    if (count === 0) continue;
    if (cumulative + count >= rank) {
      if (bucket.upperMs === null) return bucket.lowerMs;
      const fraction = Math.min(1, Math.max(0, (rank - cumulative) / count));
      return bucket.lowerMs + fraction * (bucket.upperMs - bucket.lowerMs);
    }
    cumulative += count;
  }
  return LATENCY_BUCKETS.at(-1)!.lowerMs;
}

export function aggregateLatencyDistributions(
  intervals: WorkloadMetricsInterval[],
): LatencySummary {
  const counts = emptyDistribution();
  for (const interval of intervals) {
    for (const entry of interval.entries) {
      if (entry.request_latency_distribution) {
        addDistribution(counts, entry.request_latency_distribution);
      }
    }
  }
  return summarizeCounts(counts);
}

export function summarizeLatencyCoverage(
  intervals: WorkloadMetricsInterval[],
): LatencyCoverage {
  let entriesWithDistribution = 0;
  let totalEntries = 0;
  let requestCountWithDistribution = 0;
  let totalRequestCount = 0;
  for (const interval of intervals) {
    for (const entry of interval.entries) {
      totalEntries += 1;
      totalRequestCount += validCount(entry.request_count);
      if (entry.request_latency_distribution) {
        entriesWithDistribution += 1;
        requestCountWithDistribution += validCount(entry.request_count);
      }
    }
  }
  return {
    entriesWithDistribution,
    totalEntries,
    requestCountWithDistribution,
    totalRequestCount,
    coveragePercent: totalRequestCount === 0
      ? 0
      : Math.min(100, requestCountWithDistribution / totalRequestCount * 100),
  };
}

function timelinePoint(
  timestamp: number,
  entries: WorkloadMetricsEntry[],
): LatencyTimelinePoint {
  const counts = emptyDistribution();
  for (const entry of entries) {
    if (entry.request_latency_distribution) addDistribution(counts, entry.request_latency_distribution);
  }
  const summary = summarizeCounts(counts);
  return {
    timestamp,
    ...summary,
    bucketPercentages: Object.fromEntries(LATENCY_BUCKETS.map((bucket) => [
      bucket.key,
      summary.measuredRequests === 0
        ? null
        : counts[bucket.key] / summary.measuredRequests * 100,
    ])) as Record<LatencyBucketKey, number | null>,
  };
}

export function buildLatencyTimeline(
  intervals: WorkloadMetricsInterval[],
): LatencyTimelinePoint[] {
  const sorted = [...intervals].sort((a, b) => a.start_ms - b.start_ms);
  const result: LatencyTimelinePoint[] = [];
  for (let index = 0; index < sorted.length; index += 1) {
    const interval = sorted[index];
    const previous = sorted[index - 1];
    if (previous && interval.start_ms > previous.end_ms) {
      result.push(timelinePoint(previous.end_ms, []));
    }
    result.push(timelinePoint(interval.start_ms, interval.entries));
  }
  if (sorted.length > 0) {
    const last = sorted.at(-1)!;
    result.push(timelinePoint(last.end_ms, last.entries));
  }
  return result;
}

function dimensionValue(entry: WorkloadMetricsEntry, dimension: GroupingDimension): string {
  if (dimension === 'username') return entry.username;
  if (dimension === 'resourceType') return entry.resource_type;
  if (dimension === 'action') return entry.action;
  return entry.api_label;
}

export function buildLatencyGroupComparisons(
  intervals: WorkloadMetricsInterval[],
  dimensions: GroupingDimension[],
  limit = 15,
): LatencyGroupComparison[] {
  const groups = new Map<string, { entry: WorkloadMetricsEntry; counts: RequestLatencyDistribution }>();
  for (const interval of intervals) {
    for (const entry of interval.entries) {
      if (!entry.request_latency_distribution) continue;
      const key = JSON.stringify(dimensions.map((dimension) => dimensionValue(entry, dimension)));
      const group = groups.get(key) ?? { entry, counts: emptyDistribution() };
      addDistribution(group.counts, entry.request_latency_distribution);
      groups.set(key, group);
    }
  }

  return Array.from(groups, ([key, group]) => {
    const summary = summarizeCounts(group.counts);
    return {
      key,
      label: dimensions.length === 0
        ? 'All workload'
        : dimensions.map((dimension) => dimensionValue(group.entry, dimension) || 'Unknown').join(' · '),
      groupValues: Object.fromEntries(dimensions.map((dimension) => [
        dimension,
        dimensionValue(group.entry, dimension) || 'Unknown',
      ])),
      ...summary,
      slowerThan250To1000Percent: summary.measuredRequests === 0
        ? 0
        : (summary.slowerThan250Count - summary.slowerThan1000Count)
          / summary.measuredRequests * 100,
    };
  })
    .filter((group) => group.measuredRequests > 0)
    .sort((a, b) => (
      b.slowerThan250Count - a.slowerThan250Count
      || b.measuredRequests - a.measuredRequests
      || a.key.localeCompare(b.key)
    ))
    .slice(0, limit);
}

/** Combines request/processing totals with histogram-derived latency for the
 * same workload tuple. Missing histograms stay null instead of looking fast. */
export function buildWorkloadLatencySummaries(
  intervals: WorkloadMetricsInterval[],
  dimensions: GroupingDimension[],
): WorkloadLatencySummary[] {
  const latencyByKey = new Map(
    buildLatencyGroupComparisons(intervals, dimensions, Number.MAX_SAFE_INTEGER)
      .map((summary) => [summary.key, summary]),
  );

  return buildWindowAverageRequestTimes(intervals, dimensions).map((summary) => {
    const latency = latencyByKey.get(summary.key);
    return {
      ...summary,
      measuredLatencyRequests: latency?.measuredRequests ?? 0,
      histogramP50Ms: latency?.p50Ms ?? null,
      histogramP95Ms: latency?.p95Ms ?? null,
      histogramP99Ms: latency?.p99Ms ?? null,
      slowerThan250Count: latency?.slowerThan250Count ?? 0,
      slowerThan1000Count: latency?.slowerThan1000Count ?? 0,
      slowerThan250Percent: latency?.slowerThan250Percent ?? null,
      slowerThan1000Percent: latency?.slowerThan1000Percent ?? null,
    };
  });
}
